import { useState } from 'react';
import {
  buildAlerts, deadlineInfo, fmtCcy, fmtDate, JUNIOR_AMS, SENIOR_AMS,
  startOfTodayMs, TARGET_CALLS
} from '../domain';
import type {
  Assignment, Customer, LogEntry, NewBizProspect, Quote, QuoteStatus
} from '../types';
import { AccountTypeBadge, AlertBanner, OutcomeBadge, PipelineBadge } from './Badges';
import { QuoteRowActions } from './Quotes';

type AdminTab =
  | 'performance' | 'dormant' | 'existing' | 'newbiz'
  | 'graphs' | 'table' | 'todaytargets' | 'wonlost' | 'quotes'
  | 'customers' | 'bulk';

interface Props {
  customers: Customer[];
  logs: LogEntry[];
  assignments: Assignment[];
  newbiz: NewBizProspect[];
  quotes: Quote[];
  busyMessage: string | null;
  onUpdate: (kind: 'assign' | 'newbiz', id: string) => void;
  onReassign: (kind: 'assign' | 'newbiz', id: string, currentAm: string) => void;
  onDelete: (kind: 'assign' | 'newbiz', id: string) => void;
  onOpenNewBiz: (am: string) => void;
  onAutoAssignAm: (role: 'junior' | 'senior', am: string) => void;
  onAutoAssignAll: () => void;
  onClearCycle: (am: string) => void;
  onMarkOverdueLost: () => void;
  onEditQuote: (q: Quote) => void;
  onSetQuoteStatus: (q: Quote, status: QuoteStatus) => void;
  onDeleteQuote: (q: Quote) => void;
  onDeleteCustomer: (id: string) => void;
}

const ALL_AMS = [...JUNIOR_AMS, ...SENIOR_AMS];

export function EnhancedMgmt(props: Props) {
  const [tab, setTab] = useState<AdminTab>('performance');
  const { customers: custs, logs, assignments: assigns, newbiz: nb, quotes } = props;

  return (
    <>
      <div className="section-hdr">
        <div className="section-hdr-title">Management <span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Dashboard</span></div>
        {props.busyMessage && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{props.busyMessage}</span>}
      </div>

      <div className="inner-tabs">
        {([
          ['performance',  '📊 Performance'],
          ['dormant',      '💤 Dormant'],
          ['existing',     '✅ Existing'],
          ['newbiz',       '✨ New Business'],
          ['graphs',       '📈 Graphs'],
          ['table',        '📋 Summary Table'],
          ['todaytargets', "🎯 Today's Targets"],
          ['wonlost',      '🏆 Won / Lost'],
          ['quotes',       '💬 Quotes'],
          ['customers',    '🏢 Customers'],
          ['bulk',         '⚡ Bulk Actions']
        ] as Array<[AdminTab, string]>).map(([id, label]) => (
          <button key={id} className={`itab ${tab === id ? 'on' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'performance'  && <PerformanceTab  assigns={assigns} nb={nb} custs={custs} logs={logs} quotes={quotes} />}
      {tab === 'dormant'      && <AmSectionTab title="💤 Dormant Accounts" type="dormant" amList={JUNIOR_AMS}
                                  items={assigns.filter(a => a.accountType === 'dormant')}
                                  onUpdate={props.onUpdate} onReassign={props.onReassign} onDelete={props.onDelete}
                                  onAutoAssign={(am) => props.onAutoAssignAm('junior', am)} />}
      {tab === 'existing'     && <AmSectionTab title="✅ Existing Business" type="existing" amList={ALL_AMS}
                                  items={assigns.filter(a => a.accountType === 'existing')}
                                  onUpdate={props.onUpdate} onReassign={props.onReassign} onDelete={props.onDelete}
                                  onAutoAssign={(am) => props.onAutoAssignAm(SENIOR_AMS.includes(am) ? 'senior' : 'junior', am)} />}
      {tab === 'newbiz'       && <NewBizTab nb={nb} onOpenNewBiz={props.onOpenNewBiz}
                                  onUpdate={props.onUpdate} onReassign={props.onReassign} onDelete={props.onDelete} />}
      {tab === 'graphs'       && <GraphsTab assigns={assigns} nb={nb} custs={custs} logs={logs} />}
      {tab === 'table'        && <SummaryTable assigns={assigns} nb={nb} />}
      {tab === 'todaytargets' && <AdminTodayTargets assigns={assigns} nb={nb} custs={custs} logs={logs} />}
      {tab === 'wonlost'      && <WonLostTab assigns={assigns} nb={nb} quotes={quotes}
                                  onUpdate={props.onUpdate} />}
      {tab === 'quotes'       && <QuotesTab quotes={quotes}
                                  onEdit={props.onEditQuote}
                                  onSetStatus={props.onSetQuoteStatus}
                                  onDelete={props.onDeleteQuote} />}
      {tab === 'customers'    && <CustomersTab custs={custs} logs={logs} onDelete={props.onDeleteCustomer} />}
      {tab === 'bulk'         && <BulkTab assigns={assigns} onAutoAssignAm={props.onAutoAssignAm}
                                  onAutoAssignAll={props.onAutoAssignAll} onClearCycle={props.onClearCycle}
                                  onMarkOverdueLost={props.onMarkOverdueLost} onOpenNewBiz={props.onOpenNewBiz} />}
    </>
  );
}

// ─── Performance ───────────────────────────────────────────────────────────────
function PerformanceTab({ assigns, nb, custs, logs, quotes }: {
  assigns: Assignment[]; nb: NewBizProspect[]; custs: Customer[]; logs: LogEntry[]; quotes: Quote[];
}) {
  const alerts = buildAlerts(null, assigns, nb, logs);
  const todayTs = startOfTodayMs();
  return (
    <>
      {alerts.map((a, i) => <AlertBanner key={i} type={a.type} msg={a.msg} />)}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
        {ALL_AMS.map(am => {
          const amAssigns = assigns.filter(a => a.amName === am);
          const amNb = nb.filter(a => a.amName === am);
          const amCusts = custs.filter(c => c.amName === am);
          const amLogs = logs.filter(l => amCusts.some(c => c.id === l.customerId));
          const amQuotes = quotes.filter(q => q.customerAmName === am);
          const quotesWon = amQuotes.filter(q => q.status === 'won');
          const quotesLost = amQuotes.filter(q => q.status === 'lost').length;
          const quoteRev = quotesWon.reduce((s, q) => s + (q.revenue || 0), 0);
          const all = [...amAssigns, ...amNb];
          const total = all.length;
          const called = all.filter(a => ['called', 'quoted', 'won', 'lost'].includes(a.status)).length;
          const won = all.filter(a => a.status === 'won').length;
          const rev = all.reduce((s, a) => s + (a.revenue || 0), 0) + quoteRev;
          const overdue = all.filter(a => a.deadline < Date.now() && a.status !== 'won' && a.status !== 'lost').length;
          const pct = total ? Math.round((called / total) * 100) : 0;
          const isSenior = SENIOR_AMS.includes(am);
          const roleCol = isSenior ? 'var(--accent)' : 'var(--accent2)';
          return (
            <div key={am} className="mgmt-section">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{am}</div>
                  <div style={{ fontSize: 11, color: roleCol, fontWeight: 600, marginTop: 2 }}>{isSenior ? 'Senior AM' : 'Junior AM'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: 'var(--accent)' }}>{fmtCcy(rev)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>revenue won (incl. quotes)</div>
                </div>
              </div>
              <div className="am-card-perf">
                <div className="am-card-tile"><div className="am-card-tile-val">{total}</div><div className="am-card-tile-lbl">Assigned</div></div>
                <div className="am-card-tile"><div className="am-card-tile-val" style={{ color: 'var(--call)' }}>{won}</div><div className="am-card-tile-lbl">Converted</div></div>
                <div className="am-card-tile"><div className="am-card-tile-val" style={{ color: overdue > 0 ? 'var(--danger)' : 'var(--text-dim)' }}>{overdue}</div><div className="am-card-tile-lbl">Overdue</div></div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Progress ({pct}%)</div>
              <div className="prog-bar">
                <div className="prog-fill" style={{ width: `${pct}%`, background: pct >= 70 ? 'var(--call)' : pct >= 40 ? 'var(--accent)' : 'var(--accent2)' }} />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 10, fontSize: 11, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                <span>📞 {amLogs.filter(l => l.ts >= todayTs && l.type === 'call').length} calls today</span>
                <span>📝 {amLogs.filter(l => l.ts >= todayTs).length} entries today</span>
                <span>💬 {quotesWon.length}W / {quotesLost}L quotes</span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── AmSection (used by Dormant + Existing) ────────────────────────────────────
function AmSectionTab({ title, type, items, amList, onUpdate, onReassign, onDelete, onAutoAssign }: {
  title: string; type: 'dormant' | 'existing'; items: Assignment[]; amList: string[];
  onUpdate: Props['onUpdate']; onReassign: Props['onReassign']; onDelete: Props['onDelete'];
  onAutoAssign: (am: string) => void;
}) {
  const buttons = type === 'dormant' ? JUNIOR_AMS : SENIOR_AMS;
  return (
    <>
      <div className="section-hdr">
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 19 }}>{title}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {buttons.map(am => (
            <button key={am} className="action-btn btn-call" onClick={() => onAutoAssign(am)}>⚡ Assign {am.split(' ').pop()}</button>
          ))}
        </div>
      </div>
      {amList.map(am => {
        const amItems = items.filter(a => a.amName === am);
        const called = amItems.filter(a => ['called', 'quoted', 'won', 'lost'].includes(a.status)).length;
        const won = amItems.filter(a => a.status === 'won').length;
        const overdue = amItems.filter(a => a.deadline < Date.now() && a.status !== 'won' && a.status !== 'lost').length;
        const rev = amItems.reduce((s, a) => s + (a.revenue || 0), 0);
        return (
          <div key={am} className="mgmt-section" style={{ marginBottom: 12 }}>
            <div className="section-hdr" style={{ marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{am}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {amItems.length} assigned · {called} contacted · {won} won · {fmtCcy(rev)}
                </div>
              </div>
              {overdue > 0
                ? <span className="dl-overdue">⚠️ {overdue} overdue</span>
                : <span className="dl-ok">✓ On track</span>}
            </div>
            {amItems.length === 0
              ? <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>No {type} accounts assigned yet.</p>
              : amItems.map(a => {
                const dl = deadlineInfo(a.deadline);
                return (
                  <div key={a.id} className="assign-row">
                    <div className="assign-name">{a.custName || '—'}</div>
                    <PipelineBadge status={a.status} />
                    <OutcomeBadge outcome={a.outcome} />
                    <div className={dl.cls} style={{ fontSize: 10 }}>{dl.label}</div>
                    {a.revenue ? <div style={{ fontSize: 11, color: 'var(--accent)' }}>{fmtCcy(a.revenue)}</div> : null}
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button className="entry-del-btn" onClick={() => onUpdate('assign', a.id)}>Update</button>
                      <button className="entry-del-btn" onClick={() => onReassign('assign', a.id, a.amName)}>Move</button>
                      <button className="entry-del-btn" onClick={() => onDelete('assign', a.id)}>✕</button>
                    </div>
                  </div>
                );
              })}
          </div>
        );
      })}
    </>
  );
}

// ─── New Biz tab ───────────────────────────────────────────────────────────────
function NewBizTab({ nb, onOpenNewBiz, onUpdate, onReassign, onDelete }: {
  nb: NewBizProspect[]; onOpenNewBiz: Props['onOpenNewBiz'];
  onUpdate: Props['onUpdate']; onReassign: Props['onReassign']; onDelete: Props['onDelete'];
}) {
  return (
    <>
      <div className="section-hdr">
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 19 }}>✨ New Business Prospects</div>
        <button className="action-btn btn-note" onClick={() => onOpenNewBiz('')}>+ Add Prospect</button>
      </div>
      {SENIOR_AMS.map(am => {
        const amNb = nb.filter(a => a.amName === am);
        const won = amNb.filter(a => a.status === 'won').length;
        const rev = amNb.reduce((s, a) => s + (a.revenue || 0), 0);
        return (
          <div key={am} className="mgmt-section" style={{ marginBottom: 12 }}>
            <div className="section-hdr" style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 600 }}>
                {am} <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 400 }}>({amNb.length} prospects)</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--call)' }}>{won} won · {fmtCcy(rev)}</div>
            </div>
            {amNb.length === 0
              ? <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>No new business prospects assigned.</p>
              : amNb.map(a => {
                const dl = deadlineInfo(a.deadline);
                return (
                  <div key={a.id} className="assign-row">
                    <div className="assign-name" style={{ minWidth: 150 }}>
                      <div style={{ fontWeight: 500 }}>{a.companyName || '(unnamed)'}</div>
                      {a.contact && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.contact}</div>}
                    </div>
                    <PipelineBadge status={a.status} />
                    <OutcomeBadge outcome={a.outcome} />
                    <div className={dl.cls} style={{ fontSize: 10 }}>{dl.label}</div>
                    {a.revenue ? <div style={{ fontSize: 11, color: 'var(--accent)' }}>{fmtCcy(a.revenue)}</div> : null}
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button className="entry-del-btn" onClick={() => onUpdate('newbiz', a.id)}>Update</button>
                      <button className="entry-del-btn" onClick={() => onReassign('newbiz', a.id, a.amName)}>Move</button>
                      <button className="entry-del-btn" onClick={() => onDelete('newbiz', a.id)}>✕</button>
                    </div>
                  </div>
                );
              })}
          </div>
        );
      })}
    </>
  );
}

// ─── Graphs ────────────────────────────────────────────────────────────────────
function BarChart({ title, data, color }: {
  title: string; data: Array<{ label: string; val: number; displayVal?: string }>; color: string;
}) {
  const max = Math.max(...data.map(d => d.val), 1);
  return (
    <div className="mgmt-section" style={{ marginBottom: 14 }}>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17, marginBottom: 14 }}>{title}</div>
      <div className="bar-chart">
        {data.map(d => (
          <div key={d.label} className="bar-row">
            <div className="bar-label">{d.label}</div>
            <div className="bar-track">
              <div className="bar-fill" style={{
                width: `${d.val ? Math.round((d.val / max) * 100) : 0}%`, background: color
              }}>
                {d.val ? <span>{d.displayVal ?? d.val}</span> : null}
              </div>
            </div>
            <div className="bar-val">{d.displayVal ?? d.val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GraphsTab({ assigns, nb, custs, logs }: {
  assigns: Assignment[]; nb: NewBizProspect[]; custs: Customer[]; logs: LogEntry[];
}) {
  const sumRev = (xs: { revenue: number }[]) => xs.reduce((s, a) => s + (a.revenue || 0), 0);
  const revDormant = ALL_AMS.map(am => {
    const v = sumRev(assigns.filter(a => a.amName === am && a.accountType === 'dormant'));
    return { label: am.split(' ').pop()!, val: v, displayVal: fmtCcy(v) };
  });
  const revExisting = ALL_AMS.map(am => {
    const v = sumRev(assigns.filter(a => a.amName === am && a.accountType === 'existing'));
    return { label: am.split(' ').pop()!, val: v, displayVal: fmtCcy(v) };
  });
  const revNb = ALL_AMS.map(am => {
    const v = sumRev(nb.filter(a => a.amName === am));
    return { label: am.split(' ').pop()!, val: v, displayVal: fmtCcy(v) };
  });
  const callsByAm = ALL_AMS.map(am => {
    const ids = new Set(custs.filter(c => c.amName === am).map(c => c.id));
    return { label: am.split(' ').pop()!, val: logs.filter(l => l.type === 'call' && ids.has(l.customerId)).length };
  });

  const allItems = [...assigns, ...nb];
  const total = allItems.length || 1;
  const funnel = [
    { label: 'Assigned',   count: allItems.length, col: 'rgba(91,138,240,.7)' },
    { label: 'Researched', count: allItems.filter(a => ['researched', 'called', 'followup', 'quoted', 'won', 'lost'].includes(a.status)).length, col: 'rgba(232,197,71,.7)' },
    { label: 'Called',     count: allItems.filter(a => ['called', 'followup', 'quoted', 'won', 'lost'].includes(a.status)).length, col: 'rgba(240,120,64,.7)' },
    { label: 'Quoted',     count: allItems.filter(a => ['quoted', 'won', 'lost'].includes(a.status)).length, col: 'rgba(64,210,210,.7)' },
    { label: 'Won',        count: allItems.filter(a => a.status === 'won').length, col: 'rgba(91,240,160,.7)' }
  ];

  return (
    <>
      <BarChart title="💤 Dormant Account Revenue by AM" data={revDormant} color="var(--accent2)" />
      <BarChart title="✅ Existing Business Revenue by AM" data={revExisting} color="var(--call)" />
      <BarChart title="✨ New Business Revenue by AM" data={revNb} color="var(--accent)" />
      <BarChart title="📞 Calls Completed by AM" data={callsByAm} color="rgba(155,110,240,.8)" />
      <div className="mgmt-section">
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17, marginBottom: 16 }}>🔻 Conversion Funnel</div>
        <div className="funnel">
          {funnel.map((s, i) => (
            <div key={s.label} className="funnel-step" style={{ background: s.col, width: `${100 - i * 8}%` }}>
              <span className="funnel-label">{s.label}</span>
              <span>
                <span className="funnel-count">{s.count}</span>
                <span className="funnel-pct">({Math.round((s.count / total) * 100)}%)</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Summary Table ─────────────────────────────────────────────────────────────
function SummaryTable({ assigns, nb }: { assigns: Assignment[]; nb: NewBizProspect[] }) {
  type Row = {
    am: string; role: string; type: 'dormant' | 'existing' | 'newbiz';
    items: Array<Assignment | NewBizProspect>;
  };
  const rows: Row[] = ALL_AMS.flatMap<Row>(am => {
    const isJunior = JUNIOR_AMS.includes(am);
    const role = isJunior ? 'Junior AM' : 'Senior AM';
    if (isJunior) {
      return [{ am, role, type: 'dormant', items: assigns.filter(a => a.amName === am && a.accountType === 'dormant') }];
    }
    return [
      { am, role, type: 'existing', items: assigns.filter(a => a.amName === am && a.accountType === 'existing') },
      { am, role, type: 'newbiz', items: nb.filter(a => a.amName === am) }
    ];
  });

  return (
    <div className="mgmt-section" style={{ overflowX: 'auto' }}>
      <table className="perf-table" style={{ minWidth: 900 }}>
        <thead>
          <tr>
            <th>Account Manager</th><th>Role</th><th>Type</th>
            <th style={{ textAlign: 'center' }}>Assigned</th>
            <th style={{ textAlign: 'center' }}>Researched</th>
            <th style={{ textAlign: 'center' }}>Called</th>
            <th style={{ textAlign: 'center' }}>Follow-ups</th>
            <th style={{ textAlign: 'center' }}>Quoted</th>
            <th style={{ textAlign: 'center' }}>Won</th>
            <th style={{ textAlign: 'center' }}>Lost</th>
            <th style={{ textAlign: 'right' }}>Revenue</th>
            <th style={{ textAlign: 'center' }}>Overdue</th>
            <th>Progress</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={13} style={{ textAlign: 'center', padding: 20, color: 'var(--text-dim)' }}>
              No assignments yet. Use Bulk Actions to assign accounts.
            </td></tr>
          ) : rows.map((r, i) => {
            const items = r.items;
            const researched = items.filter(a => ['researched', 'called', 'followup', 'quoted', 'won', 'lost'].includes(a.status)).length;
            const called = items.filter(a => ['called', 'followup', 'quoted', 'won', 'lost'].includes(a.status)).length;
            const followups = items.filter(a => a.status === 'followup').length;
            const quoted = items.filter(a => ['quoted', 'won'].includes(a.status)).length;
            const won = items.filter(a => a.status === 'won').length;
            const lost = items.filter(a => a.status === 'lost').length;
            const rev = items.reduce((s, a) => s + (a.revenue || 0), 0);
            const overdue = items.filter(a => a.deadline < Date.now() && a.status !== 'won' && a.status !== 'lost').length;
            const pct = items.length ? Math.round((called / items.length) * 100) : 0;
            const isSenior = SENIOR_AMS.includes(r.am);
            return (
              <tr key={`${r.am}-${r.type}-${i}`}>
                <td style={{ fontWeight: 500 }}>{r.am}</td>
                <td><span style={{ fontSize: 11, fontWeight: 700, color: isSenior ? 'var(--accent)' : 'var(--accent2)' }}>{r.role}</span></td>
                <td><AccountTypeBadge type={r.type} /></td>
                <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>{items.length}</td>
                <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: 'var(--note)' }}>{researched}</td>
                <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: 'var(--email)' }}>{called}</td>
                <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: 'var(--accent)' }}>{followups}</td>
                <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>{quoted}</td>
                <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: 'var(--call)' }}>{won}</td>
                <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: 'var(--danger)' }}>{lost}</td>
                <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace", color: 'var(--accent)' }}>{fmtCcy(rev)}</td>
                <td style={{ textAlign: 'center', color: overdue > 0 ? 'var(--danger)' : 'var(--call)' }}>
                  {overdue > 0 ? `⚠️ ${overdue}` : '✓ 0'}
                </td>
                <td>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>{pct}%</div>
                  <div className="prog-bar" style={{ width: 80 }}>
                    <div className="prog-fill" style={{
                      width: `${pct}%`,
                      background: pct >= 70 ? 'var(--call)' : pct >= 40 ? 'var(--accent)' : 'var(--accent2)'
                    }} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Admin Today's Targets ─────────────────────────────────────────────────────
function AdminTodayTargets({ assigns, nb, custs, logs }: {
  assigns: Assignment[]; nb: NewBizProspect[]; custs: Customer[]; logs: LogEntry[];
}) {
  const todayTs = startOfTodayMs();
  const now = Date.now();
  return (
    <>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 21, marginBottom: 16 }}>
        🎯 Today's Targets — <em style={{ color: 'var(--accent)' }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </em>
      </div>
      {ALL_AMS.map(am => {
        const amAssigns = assigns.filter(a => a.amName === am);
        const amNb = nb.filter(a => a.amName === am);
        const all = [...amAssigns, ...amNb];
        const amCusts = custs.filter(c => c.amName === am);
        const amLogs = logs.filter(l => amCusts.some(c => c.id === l.customerId));
        const todayLogs = amLogs.filter(l => l.ts >= todayTs);
        const todayCalls = todayLogs.filter(l => l.type === 'call').length;
        const todayEmails = todayLogs.filter(l => l.type === 'email').length;
        const todayNotes = todayLogs.filter(l => l.type === 'note').length;
        const isJunior = JUNIOR_AMS.includes(am);
        const remaining = Math.max(0, TARGET_CALLS - todayCalls);
        const pct = Math.min(100, Math.round((todayCalls / TARGET_CALLS) * 100));
        const totalAssigned = all.length;
        const researched = all.filter(a => ['researched', 'called', 'followup', 'quoted', 'won', 'lost'].includes(a.status)).length;
        const called = all.filter(a => ['called', 'followup', 'quoted', 'won', 'lost'].includes(a.status)).length;
        const followups = all.filter(a => a.status === 'followup').length;
        const overdue = all.filter(a => a.deadline < now && a.status !== 'won' && a.status !== 'lost').length;
        const won = all.filter(a => a.status === 'won').length;
        const lost = all.filter(a => a.status === 'lost').length;
        const revToday = all.filter(a => a.wonDate && a.wonDate >= todayTs).reduce((s, a) => s + (a.revenue || 0), 0);
        const revTotal = all.reduce((s, a) => s + (a.revenue || 0), 0);
        const dormCount = amAssigns.filter(a => a.accountType === 'dormant').length;
        const existCount = amAssigns.filter(a => a.accountType === 'existing').length;
        const nbCount = amNb.length;
        const roleLabel = isJunior ? 'Junior AM' : 'Senior AM';
        const roleCol = isJunior ? 'var(--accent2)' : 'var(--accent)';
        const progCol = pct >= 100 ? 'var(--call)' : pct >= 60 ? 'var(--note)' : 'var(--accent2)';
        const valCol = pct >= 100 ? 'var(--call)' : pct >= 60 ? 'var(--note)' : 'var(--danger)';
        const alerts = buildAlerts(am, assigns, nb, logs);

        const recent = [...todayLogs].sort((a, b) => b.ts - a.ts).slice(0, 5);

        return (
          <div key={am} className="mgmt-section" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20 }}>{am}</div>
                <span style={{ fontSize: 11, fontWeight: 700, color: roleCol, background: `${roleCol}22`, padding: '2px 9px', borderRadius: 5 }}>{roleLabel}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, color: 'var(--call)' }}>{fmtCcy(revTotal)}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>total revenue won</div>
                {revToday > 0 && <div style={{ fontSize: 11, color: 'var(--accent)' }}>+{fmtCcy(revToday)} today</div>}
              </div>
            </div>
            {alerts.map((a, i) => <AlertBanner key={i} type={a.type} msg={a.msg} />)}

            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Today's Call Target</div>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, color: valCol }}>{todayCalls} / {TARGET_CALLS}</div>
              </div>
              <div className="prog-bar" style={{ height: 10 }}>
                <div className="prog-fill" style={{ width: `${pct}%`, background: progCol }} />
              </div>
              <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                <span style={{ color: remaining === 0 ? 'var(--call)' : 'var(--danger)' }}>📞 {remaining} calls remaining</span>
                <span>✉️ {todayEmails} emails</span>
                <span>📝 {todayNotes} notes</span>
                <span>{todayLogs.length} total today</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8, marginBottom: 12 }}>
              <KpiTile val={totalAssigned} label="Assigned" sub={`${dormCount} dorm · ${existCount} exist · ${nbCount} NB`} />
              <KpiTile val={researched} label="Researched" col="var(--note)" />
              <KpiTile val={called} label="Called" col="var(--email)" />
              <KpiTile val={followups} label="Follow-ups" col={followups > 0 ? '#9b6ef0' : 'var(--text-dim)'} />
              <KpiTile val={overdue} label="Overdue" col={overdue > 0 ? 'var(--danger)' : 'var(--text-dim)'} bg={overdue > 0 ? 'rgba(240,91,91,.1)' : undefined} border={overdue > 0 ? '1px solid rgba(240,91,91,.25)' : undefined} />
              <KpiTile val={won} label="Won" col="var(--call)" bg="rgba(91,240,160,.07)" border="1px solid rgba(91,240,160,.2)" />
              <KpiTile val={lost} label="Lost" col="var(--danger)" bg="rgba(240,91,91,.06)" />
            </div>

            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Today's Activity ({todayLogs.length})
            </div>
            {recent.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '8px 0' }}>No activity logged today yet.</div>
              : recent.map(l => {
                const cx = amCusts.find(c => c.id === l.customerId);
                const ico = l.type === 'call' ? '📞' : l.type === 'email' ? '✉️' : '📝';
                return (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                    <span>{ico}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cx?.name ?? l.customerName ?? '—'}</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--text-dim)' }}>
                      {new Date(l.ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            {todayLogs.length > 5 && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', paddingTop: 6 }}>+{todayLogs.length - 5} more entries today</div>
            )}
          </div>
        );
      })}
    </>
  );
}

function KpiTile({ val, label, col, sub, bg, border }: {
  val: number; label: string; col?: string; sub?: string; bg?: string; border?: string;
}) {
  return (
    <div style={{ textAlign: 'center', padding: 9, background: bg ?? 'var(--surface2)', border: border ?? undefined, borderRadius: 8 }}>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: col }}>{val}</div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 9, color: 'var(--text-dim)' }}>{sub}</div>}
    </div>
  );
}

// ─── Won / Lost ────────────────────────────────────────────────────────────────
function WonLostTab({ assigns, nb, quotes, onUpdate }: {
  assigns: Assignment[]; nb: NewBizProspect[]; quotes: Quote[];
  onUpdate: Props['onUpdate'];
}) {
  type Row = (Assignment | NewBizProspect) & { _kind: 'assign' | 'newbiz'; _name: string };
  const all: Row[] = [
    ...assigns.map(a => ({ ...a, _kind: 'assign' as const, _name: a.custName })),
    ...nb.map(n => ({ ...n, _kind: 'newbiz' as const, _name: n.companyName }))
  ];
  const won = all.filter(a => a.status === 'won');
  const lost = all.filter(a => a.status === 'lost');
  const quotesWon = quotes.filter(q => q.status === 'won');
  const quotesLost = quotes.filter(q => q.status === 'lost');
  const totalRev = won.reduce((s, a) => s + (a.revenue || 0), 0)
                 + quotesWon.reduce((s, q) => s + (q.revenue || 0), 0);
  const totalDecided = won.length + lost.length + quotesWon.length + quotesLost.length;
  const winRate = totalDecided ? Math.round(((won.length + quotesWon.length) / totalDecided) * 100) : 0;

  return (
    <>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 21, marginBottom: 16 }}>
        🏆 Won &amp; Lost <em style={{ color: 'var(--accent)' }}>Accounts &amp; Quotes</em>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 11, marginBottom: 20 }}>
        <SummaryCard val={won.length + quotesWon.length} col="var(--call)" label="Total Won" />
        <SummaryCard val={lost.length + quotesLost.length} col="var(--danger)" label="Total Lost" />
        <SummaryCard val={fmtCcy(totalRev)} col="var(--accent)" label="Revenue Won" />
        <SummaryCard val={`${winRate}%`} col="var(--note)" label="Win Rate" />
      </div>

      {ALL_AMS.map(am => {
        const amWon = won.filter(a => a.amName === am);
        const amLost = lost.filter(a => a.amName === am);
        const amQuotesWon = quotesWon.filter(q => q.customerAmName === am);
        const amQuotesLost = quotesLost.filter(q => q.customerAmName === am);
        const amRev = amWon.reduce((s, a) => s + (a.revenue || 0), 0)
                    + amQuotesWon.reduce((s, q) => s + (q.revenue || 0), 0);
        const amAll = all.filter(a => a.amName === am);
        const decided = amWon.length + amLost.length + amQuotesWon.length + amQuotesLost.length;
        const amWinR = decided ? Math.round(((amWon.length + amQuotesWon.length) / decided) * 100) : 0;
        void amAll;
        const isJunior = JUNIOR_AMS.includes(am);
        const roleCol = isJunior ? 'var(--accent2)' : 'var(--accent)';
        return (
          <div key={am} className="mgmt-section" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18 }}>{am}</div>
                <span style={{ fontSize: 11, fontWeight: 700, color: roleCol }}>{isJunior ? 'Junior AM' : 'Senior AM'}</span>
              </div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <Mini val={amWon.length + amQuotesWon.length} col="var(--call)" lbl="Won" />
                <Mini val={amLost.length + amQuotesLost.length} col="var(--danger)" lbl="Lost" />
                <Mini val={`${amWinR}%`} col="var(--note)" lbl="Win Rate" />
                <Mini val={fmtCcy(amRev)} col="var(--accent)" lbl="Revenue" align="right" />
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--call)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Won ({amWon.length})
            </div>
            {amWon.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '6px 0' }}>No won accounts yet.</div>
              : [...amWon].sort((a, b) => (b.wonDate ?? 0) - (a.wonDate ?? 0)).map(a => (
                <div key={a.id} className="assign-row">
                  <div className="assign-name" style={{ minWidth: 160 }}>
                    <div style={{ fontWeight: 500 }}>{a._name || '—'}</div>
                    {a.wonDate ? <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>Won {fmtDate(a.wonDate)}</div> : null}
                  </div>
                  <AccountTypeBadge type={a._kind === 'newbiz' ? 'newbiz' : (a as Assignment).accountType} />
                  <OutcomeBadge outcome={a.outcome} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--call)' }}>{fmtCcy(a.revenue)}</div>
                  <button className="entry-del-btn" onClick={() => onUpdate(a._kind, a.id)}>Edit</button>
                </div>
              ))}

            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '12px 0 6px' }}>
              Lost ({amLost.length})
            </div>
            {amLost.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '6px 0' }}>No lost accounts.</div>
              : [...amLost].sort((a, b) => b.assignedDate - a.assignedDate).map(a => (
                <div key={a.id} className="assign-row">
                  <div className="assign-name" style={{ minWidth: 160 }}>
                    <div style={{ fontWeight: 500 }}>{a._name || '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>Assigned {fmtDate(a.assignedDate)}</div>
                  </div>
                  <AccountTypeBadge type={a._kind === 'newbiz' ? 'newbiz' : (a as Assignment).accountType} />
                  <OutcomeBadge outcome={a.outcome} />
                  {a.notes && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>
                      {a.notes.slice(0, 60)}{a.notes.length > 60 ? '…' : ''}
                    </div>
                  )}
                  <button className="entry-del-btn" onClick={() => onUpdate(a._kind, a.id)}>Review</button>
                </div>
              ))}
          </div>
        );
      })}
    </>
  );
}

function SummaryCard({ val, col, label }: { val: number | string; col: string; label: string }) {
  return (
    <div className="mgmt-section" style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: typeof val === 'string' && val.length > 6 ? 28 : 36, color: col }}>{val}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    </div>
  );
}

function Mini({ val, col, lbl, align }: { val: number | string; col: string; lbl: string; align?: 'right' }) {
  return (
    <div style={{ textAlign: align ?? 'center' }}>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: col }}>{val}</div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{lbl}</div>
    </div>
  );
}

// ─── Admin: Quotes ─────────────────────────────────────────────────────────────
function QuotesTab({ quotes, onEdit, onSetStatus, onDelete }: {
  quotes: Quote[];
  onEdit: Props['onEditQuote'];
  onSetStatus: Props['onSetQuoteStatus'];
  onDelete: Props['onDeleteQuote'];
}) {
  const open = quotes.filter(q => q.status === 'open');
  const won = quotes.filter(q => q.status === 'won');
  const lost = quotes.filter(q => q.status === 'lost');
  const totalRev = won.reduce((s, q) => s + (q.revenue || 0), 0);

  const sectionFor = (label: string, list: Quote[], col: string) => (
    <div className="mgmt-section" style={{ marginBottom: 14 }}>
      <div className="section-hdr" style={{ marginBottom: 10 }}>
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17 }}>
          {label} <span style={{ fontSize: 11, color: col }}>({list.length})</span>
        </div>
      </div>
      {list.length === 0
        ? <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>No {label.toLowerCase()} quotes.</p>
        : [...list]
            .sort((a, b) => (b.decidedTs ?? b.createdTs) - (a.decidedTs ?? a.createdTs))
            .map(q => (
              <div key={q.id} className="assign-row">
                <div className="assign-name" style={{ minWidth: 160 }}>
                  <div style={{ fontWeight: 500 }}>{q.customerName}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
                    {q.customerAmName} · ref <span style={{ fontFamily: "'DM Mono', monospace", color: 'var(--accent2)' }}>{q.clarityRef}</span>
                  </div>
                </div>
                {q.notes && <div style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>
                  {q.notes.slice(0, 60)}{q.notes.length > 60 ? '…' : ''}
                </div>}
                <div style={{ fontSize: 12, fontWeight: 600, color: q.revenue ? 'var(--call)' : 'var(--text-dim)' }}>
                  {q.revenue ? fmtCcy(q.revenue) : '£0'}
                </div>
                <QuoteRowActions
                  q={q}
                  onWin={() => onSetStatus(q, 'won')}
                  onLose={() => onSetStatus(q, 'lost')}
                  onReopen={() => onSetStatus(q, 'open')}
                  onEdit={() => onEdit(q)}
                  onDelete={() => onDelete(q)}
                />
              </div>
            ))}
    </div>
  );

  return (
    <>
      <div className="section-hdr">
        <div className="section-hdr-title">💬 Quotes</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 11, marginBottom: 18 }}>
        <SummaryCard val={open.length} col="var(--accent2)" label="Open" />
        <SummaryCard val={won.length} col="var(--call)" label="Won" />
        <SummaryCard val={lost.length} col="var(--danger)" label="Lost" />
        <SummaryCard val={fmtCcy(totalRev)} col="var(--accent)" label="Revenue Won" />
      </div>
      {sectionFor('Open', open, 'var(--accent2)')}
      {sectionFor('Won', won, 'var(--call)')}
      {sectionFor('Lost', lost, 'var(--danger)')}
    </>
  );
}

// ─── Admin: Customers (delete-capable, admin only) ─────────────────────────────
function CustomersTab({ custs, logs, onDelete }: {
  custs: Customer[]; logs: LogEntry[]; onDelete: Props['onDeleteCustomer'];
}) {
  return (
    <>
      <div className="section-hdr">
        <div className="section-hdr-title">🏢 Customers ({custs.length})</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Admin-only customer removal. Cascades to logs &amp; quotes.</div>
      </div>
      <div className="mgmt-section">
        {[...custs].sort((a, b) => a.name.localeCompare(b.name)).map(c => (
          <div key={c.id} className="customer-mgmt-item">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="customer-mgmt-name">{c.name}</div>
              <div className="customer-mgmt-sub">{c.amName} · {c.county || '—'} · {c.postcode || '—'}</div>
            </div>
            <div className="customer-mgmt-meta">
              <span>{logs.filter(l => l.customerId === c.id).length} logs</span>
              <span>2025: {fmtCcy(c.t2025)}</span>
              <button className="entry-del-btn" onClick={() => onDelete(c.id)}>Remove</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Bulk ──────────────────────────────────────────────────────────────────────
function BulkTab({
  assigns, onAutoAssignAm, onAutoAssignAll, onClearCycle, onMarkOverdueLost, onOpenNewBiz
}: {
  assigns: Assignment[];
  onAutoAssignAm: Props['onAutoAssignAm']; onAutoAssignAll: Props['onAutoAssignAll'];
  onClearCycle: Props['onClearCycle']; onMarkOverdueLost: Props['onMarkOverdueLost'];
  onOpenNewBiz: Props['onOpenNewBiz'];
}) {
  return (
    <div className="mgmt-section">
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 19, marginBottom: 16 }}>⚡ Bulk Actions</div>

      <div className="bulk-bar" style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Auto-Assign Today's Accounts</span>
        <button className="bulk-btn" onClick={onAutoAssignAll}>⚡ Assign All AMs (10+10 each)</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
        {ALL_AMS.map(am => {
          const role = SENIOR_AMS.includes(am) ? 'senior' : 'junior';
          const existing = assigns.filter(a => a.amName === am);
          const activeEx = existing.filter(a => a.deadline > Date.now() && a.status !== 'won' && a.status !== 'lost');
          return (
            <div key={am} className="mgmt-section">
              <div style={{ fontWeight: 600, marginBottom: 10 }}>{am}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                {activeEx.length} active in current cycle
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <button className="bulk-btn" onClick={() => onAutoAssignAm(role as 'junior' | 'senior', am)}>
                  ⚡ {role === 'junior' ? 'Assign 10 Dormant + 10 Existing' : 'Assign 10 Existing + 10 NB Slots'}
                </button>
                <button className="bulk-btn" onClick={() => onOpenNewBiz(am)}>✨ Add New Prospect</button>
                <button className="bulk-btn danger" onClick={() => onClearCycle(am)}>🗑 Clear Current Cycle</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mgmt-section" style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Mark All Overdue as Reviewed</div>
        <button className="bulk-btn" onClick={onMarkOverdueLost}>✓ Mark Overdue as Reviewed (Lost)</button>
      </div>
    </div>
  );
}
